import {AttrsSchema, Component, ConfigOptions, jsx} from 'panel';
import range from 'lodash/range';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {FunnelQueryResponse} from 'common/report/data/funnels/api-types';
import {FunnelBookmark} from 'common/report/models/funnels/types';
import {BarVizEl} from 'common/charts/visualization/bar-viz';
import {SegmentFilterController, SegmentSection} from 'common/charts/contexts/segment-filter-controller';
import {ChartsContextRegistry} from 'common/charts/contexts/registry';
import {
  addConversionRateToFunnel,
  bookmarkHasTimeComparison,
  BucketValueInfo,
  generateLegendSections,
  getBarChartDataFromRawFunnel,
  getStepIndexFromBookmark,
} from 'common/report/util/funnels/bar-chart';
import {Bar, BarSegment, SegmentWithoutChildren} from 'common/charts/visualization/bar-viz/types';
import {TooltipSection} from 'common/components/chart-tooltip/types';
import {getToolTipSections} from 'common/report/models/funnels/bar-chart/utils';
import {getToolTipText} from 'common/report/models/funnels/frequency-chart/utils';
import {getCalendarDateLabelsFromDates} from 'common/report/models/funnels/utils';
import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {mapBookmarkStepsToEntries} from 'common/report/models/funnels/format';
import {CustomEventDefinition} from 'common/report/util/saved-entities/types';
import {getAlphaLabelFromIndex} from 'common/util';
import {isGroupedEntry} from 'common/widgets/query-entry-section/types';
import {formatDateString} from 'common/report/util/funnels/utils';
import {DEFAULT_SELECTED_SEGMENTS_LIMIT, MAX_SELECTED_SEGMENTS_LIMIT, ReportName} from 'common/report/constants';

import css from './index.styl?inline';

interface FrequencyDistributionChartAttrs {
  'custom-events': CustomEventDefinition[];
  'project-as-of-date': string;
}

interface FrequencyDistributionChartState {
  stepIndex: number | null;
}

// TODO(sam) support any order steps and grouped steps
export class FunnelsFrequencyDistributionChart extends Component<
  FrequencyDistributionChartState,
  FrequencyDistributionChartAttrs,
  any,
  any,
  ChartsContextRegistry
> {
  private _funnelsResponse: FunnelQueryResponse;
  private _bookmark: FunnelBookmark;

  static get attrsSchema(): AttrsSchema<FrequencyDistributionChartAttrs> {
    return {
      ...super.attrsSchema,
      'custom-events': `json`,
      'project-as-of-date': `string`,
    };
  }

  connectedCallback() {
    // Have to call processData after creation of the element in the DOM tree (so the context can be grabbed from parent),
    // but before first render
    this.processData();

    super.connectedCallback();
  }

  private get segmentFilterController(): SegmentFilterController {
    return this.getContext(`segmentFilterController`);
  }

  get config(): ConfigOptions<FrequencyDistributionChartState, any, ChartsContextRegistry> {
    return {
      useShadowDom: true,
      css,
      contexts: [`segmentFilterController` as const],
      defaultContexts: {
        segmentFilterController: new SegmentFilterController({
          defaultNumSelections: DEFAULT_SELECTED_SEGMENTS_LIMIT[ReportName.Funnels],
          selectAllLimit: MAX_SELECTED_SEGMENTS_LIMIT[ReportName.Funnels],
        }),
      },
      template: () => this.renderTemplate(),
    };
  }

  public set funnelsResponse(response: FunnelQueryResponse) {
    this._funnelsResponse = response;

    this.processData();
  }

  public set bookmark(bookmark: FunnelBookmark) {
    this._bookmark = bookmark;

    this.processData();
  }

  private processData() {
    if (!(this._funnelsResponse && this._bookmark && this.isConnected)) {
      return;
    }

    const {sections, savedLegend} = generateLegendSections(this._funnelsResponse, this._bookmark);

    this.segmentFilterController.loadSections(sections);
    this.segmentFilterController.loadFromSavedLegend(savedLegend);

    addConversionRateToFunnel(this._funnelsResponse, this._bookmark);

    this.update();
  }

  private renderTemplate() {
    const selectedSections = this.segmentFilterController
      .getSections()
      .map((segmentSection: SegmentSection, i) => ({
        ...segmentSection,
        items: segmentSection.items
          .filter((v) => this.segmentFilterController.isItemSelected(i, v.id))
          .map((v) => ({...v, color: this.segmentFilterController.getItemColor(i, v.id)})),
      }))
      // Reverse is because we want the deepest segmentation to be the first in the list
      .reverse();

    const segments = getBarChartDataFromRawFunnel({
      funnelsResponse: this._funnelsResponse,
      selectedSections,
      bookmark: this._bookmark,
    });

    return (
      <div sel='.frequency-distribution-widget'>
        <BarVizEl
          attrs={{}}
          props={{
            chartData: segments,
            tooltipDataCallback: this.createTooltipDataCallback(),
          }}
        />
      </div>
    );
  }

  createTooltipDataCallback() {
    const steps = mapBookmarkStepsToEntries(this.attr(`custom-events`), this._bookmark.steps);
    const stepIndex = getStepIndexFromBookmark(this._bookmark);
    const step = stepIndex === null ? steps[steps.length - 1] : steps[stepIndex];
    let stepName;

    // TODO(sam) this partially supports any order/groups but not _really_, make it actually
    if (isGroupedEntry(step)) {
      stepName = range(0, step.event.length)
        .map((idx) => `${stepIndex + 1}${getAlphaLabelFromIndex(idx)}`)
        .join(` or `);
    } else {
      stepName = step.event.renamed || step.event.label;
    }

    return (segment: SegmentWithoutChildren, _bar: Bar, barSegment: BarSegment<BucketValueInfo>): TooltipSection[] => {
      const dataBar = barSegment;

      let compareBar;
      let compareSections;
      const hasCompareData = bookmarkHasTimeComparison(this._bookmark);
      if (hasCompareData) {
        compareBar = segment.bars[1].segments[0];
        compareSections = getToolTipText(
          compareBar.extras.count,
          compareBar.extras.percentage,
          segment.label,
          !(this._bookmark.frequency_buckets.bucket_size === 1 && dataBar.extras.bucketIdx === 0),
          stepName,
          this._bookmark.frequencyType,
        );
      }

      const sections = getToolTipText(
        dataBar.extras.count,
        dataBar.extras.percentage,
        segment.label,
        !(this._bookmark.frequency_buckets.bucket_size === 1 && dataBar.extras.bucketIdx === 0),
        stepName,
        this._bookmark.frequencyType,
      );

      return getToolTipSections(
        segment.extras.parentLabel,
        barSegment.color,
        sections,
        compareSections,
        getCalendarDateLabelsFromDates(
          DateRange.fromBookmarkFormat(this._bookmark.date_range),
          hasCompareData ? new Date(formatDateString(Object.keys(this._funnelsResponse.compare_data)[0])) : null,
          this.attr(`project-as-of-date`),
        ),
      );
    };
  }
}

export const FunnelsFrequencyDistributionChartWidgetEl = defineAndMakeJsxFactory(
  `mp-funnels-frequency-distribution-chart-widget`,
  FunnelsFrequencyDistributionChart,
);
